/**
 * @fileoverview Landing page for merchants arriving from Shopify Admin
 * @module features/shopify-app/pages/ShopifyAppLandingPage
 *
 * When a merchant clicks the app in Shopify Admin, they're redirected here.
 * This page shows the connection status and provides navigation to the dashboard.
 */

import { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authService } from '@/lib/authService'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { CheckCircle2, ExternalLink, LogIn } from 'lucide-react'

export function ShopifyAppLandingPage(): ReactElement {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isAuthenticated] = useState(() => authService.isAuthenticated())

  const shop = searchParams.get('shop')
  const shopName = shop?.replace('.myshopify.com', '') || 'your store'

  useEffect(() => {
    if (!isAuthenticated) return
    const timer = setTimeout(() => {
      navigate('/dashboard', { replace: true })
    }, 2000)
    return () => clearTimeout(timer)
  }, [isAuthenticated, navigate])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <CheckCircle2 className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-xl">AbyssBlock Analytics</CardTitle>
          <CardDescription>
            {shop ? `Connected to ${shopName}` : 'Shopify App'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isAuthenticated ? (
            <>
              <p className="text-center text-sm text-muted-foreground">
                Pixel is active on your store. Redirecting to dashboard...
              </p>
              <Button
                className="w-full"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <p className="text-center text-sm text-muted-foreground">
                Sign in to view your analytics and attribution data.
              </p>
              <Button
                className="w-full"
                onClick={() => navigate('/')}
              >
                <LogIn className="mr-2 h-4 w-4" />
                Sign In
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Don't have an account?{' '}
                <a href="/signup" className="text-primary hover:underline">
                  Sign up
                </a>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
